/**
 * 比價單位 normalize（v0.2 G-full）
 *
 * 同品項不同包裝規格要 normalize 到 base unit 才能比價：
 * - 蛋盒 10 入 NT$ 100 → 每顆 NT$ 10
 * - 鮮乳 900ml NT$ 95 → 每 100ml NT$ 10.56
 *
 * Multiplier 規則（依 contentUnit 自動推）：
 *   ml / g → ×100（每 100ml / 100g）
 *   kg / L → ×100（每 100g / 100ml，自動換算）
 *   其他（顆 / 個 / 包 / 入 / 抽 / 卷 / 片 / 把 / 條 / 根 / 串 / 盒 / 袋）→ ×1（每 1 個）
 */

export interface NormalizedPrice {
  /** 每比較單位的價格（捨入到小數 2 位） */
  pricePerCompareUnit: number;
  /** 比較單位的顯示字串（例 "100ml"、"100g"、"顆"） */
  compareUnitLabel: string;
}

const PER_100_UNITS = new Set(['ml', 'g', '公克', '毫升']);
const KILO_UNITS: Record<string, { factor: number; baseUnit: string }> = {
  kg: { factor: 1000, baseUnit: 'g' },
  公斤: { factor: 1000, baseUnit: 'g' },
  l: { factor: 1000, baseUnit: 'ml' },
  L: { factor: 1000, baseUnit: 'ml' },
  公升: { factor: 1000, baseUnit: 'ml' },
};

/**
 * 算 normalized price。回傳 undefined 代表無法比價（缺包裝資訊）。
 *
 * unitPrice 是「每包裝單位（罐/盒/包）」價格。
 * packageSize × packageContentUnit 是包裝內含多少 base unit。
 *
 * 加保護：若 packageContentUnit 與 packagingUnit 相同字（例如「1 盒裝 1 盒」），
 * 邏輯上 normalize 結果 = unitPrice，沒意義 → 回 undefined 讓 UI 不顯示。
 */
export function normalizeUnitPrice(
  unitPrice: number | undefined,
  packageSize: number | undefined,
  packageContentUnit: string | undefined,
  packagingUnit?: string,
): NormalizedPrice | undefined {
  if (unitPrice === undefined || unitPrice < 0) return undefined;
  if (packageSize === undefined || packageSize <= 0) return undefined;
  const rawUnit = (packageContentUnit ?? '').trim();
  if (!rawUnit) return undefined;
  // 同字保護：「1 盒裝 1 盒」之類的同義 normalize 回 undefined
  if (packagingUnit && packagingUnit.trim() === rawUnit) return undefined;

  // kg / L 換算成 g / ml 後再走 PER_100 路徑
  const kilo = KILO_UNITS[rawUnit];
  const sizeInBase = kilo ? packageSize * kilo.factor : packageSize;
  const baseUnit = kilo ? kilo.baseUnit : rawUnit;

  if (PER_100_UNITS.has(baseUnit)) {
    const per100 = (unitPrice / sizeInBase) * 100;
    return {
      pricePerCompareUnit: round2(per100),
      compareUnitLabel: `100${baseUnit}`,
    };
  }

  // 一般單位 → 每 1 顆 / 包 / 入 / 抽 / 卷 / 片 / 把 / ...
  const per1 = unitPrice / sizeInBase;
  return {
    pricePerCompareUnit: round2(per1),
    compareUnitLabel: baseUnit,
  };
}

/**
 * 比較兩筆 normalized price 的差異百分比。
 * 回傳正值 = 變便宜，負值 = 變貴；undefined = 無法比較。
 */
export function priceDeltaPct(
  current: NormalizedPrice | undefined,
  previous: NormalizedPrice | undefined,
): number | undefined {
  if (!current || !previous) return undefined;
  if (current.compareUnitLabel !== previous.compareUnitLabel) return undefined;
  if (previous.pricePerCompareUnit <= 0) return undefined;
  const diff =
    (previous.pricePerCompareUnit - current.pricePerCompareUnit) /
    previous.pricePerCompareUnit;
  return Math.round(diff * 1000) / 10; // 一位小數
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * 「用一份」的 default step。對應使用者心智模型：
 * - 牛奶（ml）一次喝 100ml
 * - 米（g）一次煮 100g
 * - 蛋（顆）一次吃 1 顆
 * - 汽水（瓶）一次喝 1 瓶
 *
 * 規則同 normalizeUnitPrice：ml/g（含中文同義）→ 100；其他 → 1。
 */
export interface ConsumeStep {
  amount: number;
  label: string;
}

export function consumeStep(unit: string | undefined): ConsumeStep {
  const u = (unit ?? '').trim();
  if (!u) return { amount: 1, label: '1' };
  if (PER_100_UNITS.has(u)) return { amount: 100, label: `100${u}` };
  return { amount: 1, label: `1${u}` };
}
